import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "../components/Icon";
import { WebVideoArt } from "../components/WebVideoArt";
import { WebVideo } from "../components/WebVideoCard";
import { getApiBaseUrl } from "../../api/client";
import { videoMinutes } from "../formatDuration";
import { publicSeriesApi, SeriesDetail, SeriesEpisode } from "../../api/series";

type Props = {
  seriesId: string;
  // Start playback of `episode` with `queue` (the whole show, in order) so the
  // player can auto-advance to the next episode.
  onPlayEpisode: (episode: WebVideo, queue: WebVideo[]) => void;
  onBack: () => void;
};

const abs = (url?: string | null): string | null => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${getApiBaseUrl()}${url}`;
};

export function WebSeriesDetail({ seriesId, onPlayEpisode, onBack }: Props) {
  const [series, setSeries] = useState<SeriesDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [season, setSeason] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    publicSeriesApi
      .get(seriesId)
      .then((s) => {
        if (!cancelled) setSeries(s);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this show.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [seriesId]);

  const cat = series?.categories[0] || "Show";

  // The whole show in play order → the auto-advance queue. Each episode maps to
  // the WebVideo shape the player expects (category/age come from the show).
  const queue: WebVideo[] = useMemo(() => {
    if (!series) return [];
    return series.episodes.map((ep) => ({
      id: ep.id,
      title: ep.title,
      category: series.categories[0] || "",
      duration: ep.duration,
      isPremium: ep.isPremium,
      thumbUrl: ep.thumbUrl || null,
      ageMin: series.ageMin,
      ageMax: series.ageMax,
    }));
  }, [series]);

  // Episodes grouped by season (null → a single unnumbered season).
  const seasons = useMemo(() => {
    if (!series) return [];
    const map = new Map<number, SeriesEpisode[]>();
    for (const ep of series.episodes) {
      const s = ep.seasonNumber ?? 1;
      if (!map.has(s)) map.set(s, []);
      map.get(s)!.push(ep);
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [series]);

  const multiSeason = seasons.length > 1;
  const activeSeason = season ?? seasons[0]?.[0] ?? 1;
  const shownEpisodes = useMemo(
    () => seasons.find(([n]) => n === activeSeason)?.[1] ?? [],
    [seasons, activeSeason],
  );

  const play = (ep: SeriesEpisode) => {
    const v = queue.find((q) => q.id === ep.id);
    if (v) onPlayEpisode(v, queue);
  };

  const Back = (
    <div className="wsd-top">
      <button className="wsd-back" onClick={onBack} aria-label="Back to home">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 5l-7 7 7 7"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Back
      </button>
      <span className="wsd-crumb">
        Home &nbsp;›&nbsp; <b>TV Shows</b>
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="wsd fadeup">
        {Back}
        <div className="wsd-sec" style={{ color: "var(--ink-3)" }}>
          Loading show…
        </div>
      </div>
    );
  }
  if (error || !series) {
    return (
      <div className="wsd fadeup">
        {Back}
        <div className="wsd-sec" style={{ color: "var(--ink-3)" }}>
          {error || "Show not found."}
        </div>
      </div>
    );
  }

  const poster = abs(series.thumbUrl);

  return (
    <div className="wsd fadeup">
      {Back}

      {/* Hero */}
      <section className="wsd-hero">
        <div className="wsd-poster">
          <WebVideoArt category={cat} />
          {poster && <img src={poster} alt="" />}
          <span className="pill">Series</span>
        </div>
        <div>
          <div className="wsd-eyebrow">{cat} · Series</div>
          <h1 className="wsd-title">{series.title}</h1>
          <div className="wsd-chips">
            <span className="wsd-chip">
              {series.episodeCount} episode{series.episodeCount === 1 ? "" : "s"}
            </span>
            <span className="wsd-chip">
              Age {series.ageMin}–{series.ageMax}
            </span>
            {series.isPremium && <span className="wsd-chip prem">Kidly+</span>}
          </div>
          {series.description && <p className="wsd-desc">{series.description}</p>}
          {queue.length > 0 && (
            <div className="btn-row">
              <button className="btn" onClick={() => play(series.episodes[0])}>
                <Icon name="play" size={16} /> Play episode 1
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Episodes */}
      <div className="wsd-sec">
        <div className="wsd-sec-h">
          <h2>{multiSeason ? `Season ${activeSeason}` : "Episodes"}</h2>
          {multiSeason && (
            <div className="wsd-tabs" role="tablist" aria-label="Seasons">
              {seasons.map(([n]) => (
                <button
                  key={n}
                  role="tab"
                  aria-selected={n === activeSeason}
                  className={"wsd-tab" + (n === activeSeason ? " on" : "")}
                  onClick={() => setSeason(n)}
                >
                  Season {n}
                </button>
              ))}
            </div>
          )}
        </div>

        {shownEpisodes.length === 0 ? (
          <p style={{ color: "var(--ink-3)" }}>No episodes yet.</p>
        ) : (
          <div className="wsd-eps">
            {shownEpisodes.map((ep, i) => {
              const epPoster = abs(ep.thumbUrl);
              return (
                <button key={ep.id} className="wsd-ep" onClick={() => play(ep)}>
                  <span className="wsd-ep-num">{ep.episodeNumber ?? i + 1}</span>
                  <div className="wsd-thumb">
                    <WebVideoArt category={cat} />
                    {epPoster && <img src={epPoster} alt="" />}
                    <span className="play">
                      <Icon name="play" size={15} />
                    </span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p className="wsd-ep-title">{ep.title}</p>
                    <div className="wsd-ep-sub">
                      {multiSeason
                        ? `Season ${ep.seasonNumber ?? 1} · Episode ${ep.episodeNumber ?? i + 1}`
                        : `Episode ${ep.episodeNumber ?? i + 1}`}
                    </div>
                  </div>
                  <div className="wsd-ep-end">
                    {ep.isPremium && (
                      <span className="wsd-lock" title="Kidly+">
                        <Icon name="lock" size={15} />
                      </span>
                    )}
                    <span className="wsd-ep-dur">
                      {videoMinutes(ep.duration)} min
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default WebSeriesDetail;
